'use client';

/**
 * BottomNav -- mobile-only fixed bottom tab bar (5 tabs).
 *
 * Tabs: Home, Circles, Add (center FAB), Library, You.
 * The center "Add" tab triggers an onAddPress callback (opens QuickAddSheet).
 * Active tab uses coral brand color + a Framer Motion sliding dot indicator.
 * The dot uses `layoutId="tab-dot"` so Framer animates it smoothly between
 * whichever tab is active — no manual transition math needed.
 * Frosted glass background. Visible only below md breakpoint; desktop uses AppNav.
 */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { House, Pin, Plus, List, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

const TABS = [
  { label: 'Home',    href: '/dashboard', icon: House,    exact: true  },
  { label: 'Pinned',  href: '/pinned',   icon: Pin,      exact: false },
  { label: 'Add',     href: null,         icon: Plus,     exact: false },
  { label: 'Lists',   href: '/lists',   icon: List,     exact: false },
  { label: 'Circles', href: '/circles',   icon: Users,    exact: false },
] as const;

interface BottomNavProps {
  onAddPress?: () => void;
  /** Show a draft-resume badge on the Add button (S3.8). */
  hasDraft?: boolean;
}

export function BottomNav({ onAddPress, hasDraft = false }: BottomNavProps) {
  const pathname = usePathname();

  function isActive(href: string | null, exact: boolean): boolean {
    if (!href) return false;
    if (exact) return pathname === href;
    return pathname === href || pathname.startsWith(href + '/');
  }

  return (
    <nav
      aria-label="Main navigation"
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)] bg-background/90 backdrop-blur-xl border-t-2 border-border"
    >
      <div className="flex h-16 items-end">
        {TABS.map(({ label, href, icon: Icon, exact }) => {
          const active = isActive(href, exact);
          const isAddButton = href === null;

          if (isAddButton) {
            return (
              <button
                key="add"
                onClick={onAddPress}
                className="flex-1 flex flex-col items-center pb-3"
                aria-label={hasDraft ? 'Resume adding items' : 'Quick add'}
              >
                {/* Outer div holds the -16px vertical lift so retro-fab:active
                    only needs to apply the x/y press delta without fighting
                    the Tailwind translate utility. */}
                <div className="-translate-y-4">
                  <span
                    className="relative flex items-center justify-center h-14 w-14 rounded-full bg-primary text-primary-foreground retro-fab"
                    style={{ border: '2px solid var(--primary-dark)' }}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2.5} />
                    {hasDraft && (
                      <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-accent text-accent-foreground text-[9px] font-bold flex items-center justify-center">
                        !
                      </span>
                    )}
                  </span>
                </div>
              </button>
            );
          }

          return (
            <Link
              key={href}
              href={href!}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex-1 flex flex-col items-center gap-0.5 pt-2 pb-3 text-[10px] font-medium transition-colors',
                active ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <Icon className="h-5 w-5 shrink-0" strokeWidth={active ? 2.5 : 1.75} />
              <span>{label}</span>

              {/*
               * Sliding indicator dot — shared across all tab links via layoutId.
               * Framer Motion animates the dot from its previous position to the
               * new active tab automatically. AnimatePresence handles mount/unmount.
               * Color-only fallback (text-primary) is still applied above.
               */}
              <AnimatePresence>
                {active && (
                  <motion.span
                    layoutId="tab-dot"
                    className="absolute bottom-1.5 h-1 w-1 rounded-full bg-primary"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    aria-hidden="true"
                  />
                )}
              </AnimatePresence>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
